import { useRef, useState, useEffect } from "react"
import { Mic, Square, Loader2, Play, Pause, Trash2 } from "lucide-react"
import { uploadReportAudio } from "../lib/supabase"

export interface SiteSenseBlockProps {
  audioUrl: string | null
  onAudioUrlChange: (url: string | null) => void
  disabled?: boolean
  uploadPrefix?: string
}

export function SiteSenseBlock({
  audioUrl,
  onAudioUrlChange,
  disabled = false,
  uploadPrefix = "operator",
}: SiteSenseBlockProps) {
  const [recording, setRecording] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (recording) {
      setElapsed(0)
      timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000)
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
      setElapsed(0)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [recording])

  function formatTime(s: number): string {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, "0")}`
  }

  async function startRecording() {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data)
      }
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        if (chunksRef.current.length === 0) return
        const blob = new Blob(chunksRef.current, { type: "audio/webm" })
        setUploading(true)
        try {
          const url = await uploadReportAudio(blob, { prefix: uploadPrefix })
          onAudioUrlChange(url)
        } catch (e) {
          setError(e instanceof Error ? e.message : "Upload failed")
        } finally {
          setUploading(false)
        }
      }
      recorder.start()
      mediaRecorderRef.current = recorder
      setRecording(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Microphone access denied or unavailable.")
    }
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== "inactive") {
      recorder.stop()
      mediaRecorderRef.current = null
    }
    setRecording(false)
  }

  function togglePlayback() {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      audio.currentTime = 0
    } else {
      audio.play().catch(() => {})
    }
    setPlaying(!playing)
  }

  function handleRemove() {
    onAudioUrlChange(null)
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setPlaying(false)
  }

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
      {/* Terminal label */}
      <div className="px-3 py-2 border-b border-white/[0.06]">
        <span className="font-mono text-[9px] text-white/30">
          site.sense() — <span className="text-white/50">What do you see?</span>
        </span>
      </div>

      <div className="p-3">
        {error && (
          <div className="text-[11px] font-mono text-red-400/90 bg-red-500/10 border border-red-400/20 px-3 py-2 rounded-lg mb-3">
            {error}
          </div>
        )}

        {!audioUrl ? (
          <div className="flex items-center gap-3">
            {!recording ? (
              <button
                type="button"
                disabled={disabled || uploading}
                onClick={startRecording}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[11px] font-bold uppercase tracking-wider hover:bg-teal-500/25 disabled:opacity-50 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
                {uploading ? "Uploading\u2026" : "Record"}
              </button>
            ) : (
              <div className="flex items-center gap-3 w-full">
                <button
                  type="button"
                  onClick={stopRecording}
                  className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-red-400/25 bg-red-500/15 text-red-400 font-mono text-[11px] font-bold uppercase tracking-wider hover:bg-red-500/25 transition-colors"
                >
                  <Square className="w-3.5 h-3.5" />
                  Stop
                </button>
                {/* Recording indicator */}
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-400" />
                  </span>
                  <span className="font-mono text-[12px] text-red-400/80 tabular-nums">
                    {formatTime(elapsed)}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <audio
              ref={audioRef}
              src={audioUrl}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              className="hidden"
            />
            <button
              type="button"
              onClick={togglePlayback}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/60 font-mono text-[11px] hover:bg-white/[0.08] hover:text-white/80 transition-colors"
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              {playing ? "Pause" : "Play"}
            </button>
            {/* Visual waveform hint */}
            <div className="flex items-center gap-px flex-1 h-5 px-2">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-150 ${
                    playing ? "bg-teal-400/60" : "bg-white/10"
                  }`}
                  style={{
                    height: `${20 + Math.sin(i * 0.8) * 40 + Math.random() * 20}%`,
                    transitionDelay: playing ? `${i * 30}ms` : "0ms",
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              aria-label="Remove recording"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
